import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ThreeDVisualization } from '@/components/3d-visualization';
import { Card } from '@/components/ui/card';
import { Lightbulb, Info } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useGetDevicesByIdQuery } from '@/services';
import { toast } from '@/components/ui/toaster';

export const Route = createFileRoute('/_authenticated/(user-dashboard)/dashboard/_dashboard/3d-map')({
  component: RouteComponent,
});

function RouteComponent() {
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch selected device details if needed
  const { data: deviceData } = useGetDevicesByIdQuery(
    { id: selectedDeviceId || '', includeSensors: 'true' },
    { skip: !selectedDeviceId },
  );

  const handleDeviceSelect = (deviceId: string, tab?: string) => {
    setSelectedDeviceId(deviceId);

    // Navigate to device details page with optional tab parameter
    const deviceName = deviceData?.data?.name || `Device ${deviceId.substring(0, 8)}`;

    if (tab === 'alerts') {
      // Direct navigation to the alerts tab
      navigate({
        to: `/dashboard/devices/$deviceId`,
        params: { deviceId },
        search: { tab: 'alerts' },
      });

      toast.info(`Navigating to alerts for ${deviceName}`);
    } else {
      // Just show a toast with option to navigate
      toast.info(`Device selected: ${deviceName}`, {
        description: 'Click "View Details" for more information',
        action: {
          label: 'View Details',
          onClick: () =>
            navigate({
              to: `/dashboard/devices/$deviceId`,
              params: { deviceId },
            }),
        },
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">3D Visualization</h2>
      </div>

      <div className="rounded-md">
        <ThreeDVisualization className="w-full" onDeviceSelect={handleDeviceSelect} />
      </div>

      <Card className="p-4 bg-muted/30">
        <div className="flex items-start gap-3">
          <Lightbulb className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Navigation Tips</p>
            <p>
              Visualize your IoT devices and sensor data in 3D environments. Sensors are connected to their respective
              attachment points on the models using arrows for better context.
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Left-click + drag to rotate the view</li>
              <li>Right-click + drag to pan</li>
              <li>Scroll to zoom in/out</li>
              <li>Hover over a sensor box to see detailed information</li>
              <li>Use the "Attached/Detached" toggle to show/hide connection arrows</li>
              <li>The colored arrows indicate which part of the model each sensor is monitoring</li>
              <li>If you get lost, use the "Reset View" button to recenter</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Additional information about real-time features */}
      <Card className="p-4 bg-primary/10 border-primary/20">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium mb-1">EV Charger Monitoring</p>
            <p className="text-muted-foreground">
              This 3D visualization shows real-time data from your EV charging stations. The visualization provides
              insights into:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-muted-foreground">
              <li>Charging power output and efficiency</li>
              <li>Voltage and current readings</li>
              <li>Temperature monitoring of key components</li>
              <li>Energy consumption metrics</li>
              <li>Real-time status indicators</li>
            </ul>
            <p className="mt-2 text-muted-foreground">
              Toggle "Live Data" for real-time updates or use the timeline to review historical data.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
